import { FontMatchDto } from '@tailored-pdf/api';

/**
 * Stable id of the font every unmatched embedded font falls back to. Must stay
 * in sync with the backend's DEFAULT_FALLBACK_FONT (PdfEditor.java) so client
 * rendering matches what the server embeds on save. Ids are plain slugs; the
 * CSS families are derived client-side (see fontLoader.documentFontFamily).
 */
export const FALLBACK_FONT_ID = 'noto-sans';

/**
 * Same normalization the backend's FontService applies: drop the six-letter
 * subset prefix (e.g. "BAAAAA+"), lowercase, strip non-alphanumerics.
 */
const normalizeFontName = (name: string): string =>
  name
    .replace(/^[A-Z]{6}\+/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/**
 * The /fonts/matches response flattened into a lookup keyed by the PDF's
 * PostScript font name. `null` means the server saw the font but has no
 * catalog match — the caller decides between keeping the embedded face and
 * the fallback family.
 */
export type FontMatchMap = Record<string, string | null>;

/**
 * Flattens the /fonts/matches response into a FontMatchMap. Normalized keys
 * are indexed too: the backend reads font names via PDFBox (BaseFont) while
 * the client reads them via pdf.js (which may prefer FontDescriptor.FontName)
 * — the raw strings can differ.
 */
export const buildFontMatchMap = (matches: FontMatchDto[]): FontMatchMap => {
  const map: FontMatchMap = {};
  matches.forEach(({ embeddedFontName, defaultFontId }) => {
    map[embeddedFontName] = defaultFontId ?? null;
  });
  matches.forEach(({ embeddedFontName, defaultFontId }) => {
    const normalized = normalizeFontName(embeddedFontName);
    if (!(normalized in map)) {
      map[normalized] = defaultFontId ?? null;
    }
  });
  return map;
};

/**
 * Looks an embedded PostScript name up in the match map (raw key first, then
 * normalized). Returns the catalog font id, `null` when the server saw the
 * font but nothing matched, or `undefined` when the name is unknown to the
 * map (the client derived a name the server never reported).
 */
export const matchKnownFontId = (matches: FontMatchMap, embeddedFontName: string): string | null | undefined => {
  if (embeddedFontName in matches) return matches[embeddedFontName];
  const normalized = normalizeFontName(embeddedFontName);
  return normalized in matches ? matches[normalized] : undefined;
};

/**
 * Trailing style/foundry tokens PostScript names glue onto the family name
 * ("RobotoBold", "ArialBoldMT"). Mirrors the backend's STYLE_SUFFIXES —
 * longest-first so "extrabold" is stripped whole, never leaving "extra".
 */
const STYLE_SUFFIXES = [
  'extralight', 'ultralight', 'extrabold', 'ultrabold', 'semibold', 'demibold',
  'oblique', 'regular', 'italic', 'medium', 'light', 'heavy', 'black',
  'psmt', 'bold', 'thin', 'mt', 'ps',
];

const stripStyleSuffixes = (normalized: string): string => {
  let result = normalized;
  let changed = true;
  while (changed) {
    changed = false;
    for (const suffix of STYLE_SUFFIXES) {
      // never strip a token that is the whole remaining name
      if (result.length > suffix.length && result.endsWith(suffix)) {
        result = result.slice(0, -suffix.length);
        changed = true;
        break;
      }
    }
  }
  return result;
};

export interface EmbeddedFontDescription {
  /** Normalized family key ("BAAAAA+Roboto-BoldItalic" → "roboto"). Only used to group items of one family. */
  family: string;
  isBold: boolean;
  isItalic: boolean;
}

/**
 * Derives style flags and a family grouping key from a raw pdf.js font object.
 * pdf.js's own bold/italic flags (computed from the font descriptor) win; the
 * PostScript name is the fallback signal. Actual name→known-font resolution is
 * the server's job (/fonts/matches) — this covers only what the server response
 * can't: per-item bold/italic styling and line-merge grouping.
 */
export const describeEmbeddedFont = (font: { name?: string; bold?: boolean; italic?: boolean }): EmbeddedFontDescription => {
  const psName = font.name ?? '';
  const normalized = normalizeFontName(psName);
  return {
    family: stripStyleSuffixes(normalized),
    isBold: font.bold ?? /bold|black|heavy|semibold|demibold/i.test(psName),
    isItalic: font.italic ?? /italic|oblique/i.test(psName),
  };
};

/**
 * Human-readable picker label for an embedded font: subset prefix and trailing
 * style tokens dropped, camelCase split ("BAAAAA+TimesNewRomanPS-BoldMT" →
 * "Times New Roman"). Presentation-only — never used for matching.
 */
export const embeddedFontLabel = (psName: string): string => {
  let name = psName.replace(/^[A-Z]{6}\+/, '');
  let changed = true;
  while (changed) {
    changed = false;
    name = name.replace(/[-_,. ]+$/, '');
    for (const suffix of STYLE_SUFFIXES) {
      if (name.length > suffix.length && name.toLowerCase().endsWith(suffix)) {
        name = name.slice(0, -suffix.length);
        changed = true;
        break;
      }
    }
  }
  const label = name
    .replace(/[-_,.]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return label || psName.replace(/^[A-Z]{6}\+/, '');
};

/**
 * Picker label for a single embedded face. `embeddedFontLabel` yields the bare
 * family ("Arial"); the style is appended from the descriptor flags rather than
 * re-read off the PostScript name, so a bold face whose name carries no "Bold"
 * token still labels correctly. Every face is its own picker entry, so the
 * style has to be in the label because two faces of one family are otherwise
 * indistinguishable in the list.
 */
export const embeddedFaceLabel = (psName: string, isBold: boolean, isItalic: boolean): string => {
  const family = embeddedFontLabel(psName);
  const style = `${isBold ? ' Bold' : ''}${isItalic ? ' Italic' : ''}`;
  return family + style;
};

const warnedMisses = new Set<string>();

/** The pdf.js font-object fields the embedded-face decisions need (copy them explicitly — pdf.js v6 props are prototype getters). */
export interface EmbeddedFontMeta {
  /** PostScript name (fontObj.name) — drives the server match lookup and the save payload. */
  name?: string;
  composite?: boolean;
  missingFile?: boolean;
  isType3Font?: boolean;
}

/**
 * Whether the pdf.js FontFace registered for this font can safely render
 * user-visible HTML text. pdf.js re-encodes fonts around the document's
 * charcodes; for simple embedded fonts charcode ≈ Unicode and the face renders
 * correctly (the same bet pdf.js's own text layer makes). Composite (Type0/CID)
 * and Type3 fonts may map Unicode input to wrong glyphs, and missingFile means
 * pdf.js substituted a standard font — those keep the fallback family.
 */
export const canUseEmbeddedFace = (font: EmbeddedFontMeta): boolean =>
  !font.composite && !font.missingFile && !font.isType3Font;

/**
 * How an embedded text item renders/saves once server matches are known.
 * `useEmbeddedFace: true` keeps the item on its document-embedded face, with
 * the matched catalog font (when any) and the fallback family behind it —
 * embedded → matched → fallback, per glyph. `useEmbeddedFace: false` moves the
 * item to a catalog font outright (faces that can't render Unicode HTML text).
 */
export type EmbeddedItemFontResolution =
  | { useEmbeddedFace: true; matchedFontId: string | null }
  | { useEmbeddedFace: false; fontId: string };

/**
 * Decides what an embedded text item renders/saves as once server matches are
 * known. A usable face is always kept — visual fidelity of existing document
 * text beats even a close catalog match — and text is styled as
 * `<pdf.js family>, [<matched family>,] <fallback family>` so the browser
 * substitutes per glyph, mirroring the server's per-character fallback chain
 * on save. Unusable faces (composite/Type3/substituted) go to the matched
 * catalog font, or the fallback family when nothing matched.
 */
export const resolveEmbeddedItemFont = (
  matches: FontMatchMap,
  internalFontName: string,
  font: EmbeddedFontMeta,
): EmbeddedItemFontResolution => {
  const lookupName = font.name ?? internalFontName;
  const matched = matchKnownFontId(matches, lookupName);
  if (matched === undefined && !warnedMisses.has(lookupName)) {
    // The server mapping covers every font it saw in the file — a miss here
    // means the client derived a name the server never reported. Worth a trace.
    warnedMisses.add(lookupName);
    console.warn(`No font match entry for embedded font "${lookupName}"`);
  }
  if (canUseEmbeddedFace(font)) {
    return { useEmbeddedFace: true, matchedFontId: matched ?? null };
  }
  return { useEmbeddedFace: false, fontId: matched ?? FALLBACK_FONT_ID };
};
